import React, { useState } from 'react'
import { Dialog } from '@mui/material'
import { X } from 'react-feather'
import { Button } from '@shopflo/ui'

interface TermsAndConditionsDialogV2Props {
  isOpen: boolean
  onClose: () => void
  pdfUrl: string
  onAccept: () => Promise<void>
}

const TermsAndConditionsDialogV2: React.FC<TermsAndConditionsDialogV2Props> = ({
  isOpen,
  onClose,
  pdfUrl,
  onAccept
}) => {
  const [isChecked, setIsChecked] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)

  const handleAccept = async () => {
    if (!isChecked) return
    setIsAccepting(true)
    try {
      await onAccept()
      onClose()
    } finally {
      setIsAccepting(false)
    }
  }

  const handleClose = () => {
    if (isAccepting) return
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '800px',
          maxWidth: '800px',
          overflow: 'hidden',
          boxShadow:
            '0px 54px 80px -5px rgba(0,0,0,0.15), 0px 12px 18px rgba(0,0,0,0.05), 0px 4px 6px rgba(0,0,0,0.03), 0px 0px 0px 1.2px rgba(0,0,0,0.04)'
        }
      }}
    >
      {/* Header */}
      <div className='flex items-start justify-between px-5 pt-5 pb-3 bg-white border-b border-zinc-100'>
        <div>
          <h2 className='text-base font-semibold text-zinc-900 tracking-tight'>Merchant Agreement</h2>
          <p className='text-text-xs-medium text-zinc-500 mt-0.5'>
            Please review the agreement below and accept to continue using the platform
          </p>
        </div>
        <button
          onClick={handleClose}
          disabled={isAccepting}
          className='p-1.5 rounded-lg hover:bg-zinc-100 transition-colors disabled:opacity-50 ml-3 flex-shrink-0'
          aria-label='Close dialog'
        >
          <X size={16} className='text-zinc-500' />
        </button>
      </div>

      {/* PDF iframe */}
      <div className='bg-zinc-50 border-b border-zinc-200' style={{ height: '480px' }}>
        <iframe
          src={`${pdfUrl}`}
          title='Merchant Agreement'
          className='w-full h-full'
          style={{ border: 'none' }}
        />
      </div>

      {/* Checkbox */}
      <div className='flex items-center gap-3 px-5 py-3 border-b border-zinc-100 bg-white'>
        <input
          id='tnc-checkbox'
          type='checkbox'
          checked={isChecked}
          onChange={e => setIsChecked(e.target.checked)}
          className='w-4 h-4 rounded border-zinc-300 accent-zinc-900 cursor-pointer'
        />
        <label htmlFor='tnc-checkbox' className='text-text-xs-medium text-zinc-600 cursor-pointer select-none'>
          I have read and agree to the Merchant Agreement
        </label>
      </div>

      {/* Footer */}
      <div className='flex items-center justify-end gap-2 px-5 py-4 bg-white'>
        <Button
          id='tnc-skip-button'
          variant='secondary'
          size='md'
          onClick={handleClose}
          disabled={isAccepting}
        >
          Skip for now
        </Button>
        <Button
          id='tnc-accept-button'
          variant='primary'
          size='md'
          onClick={handleAccept}
          disabled={!isChecked || isAccepting}
          loading={isAccepting}
        >
          Accept &amp; Continue
        </Button>
      </div>
    </Dialog>
  )
}

export default TermsAndConditionsDialogV2
